import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import type { ClientProfile, TicketDetail } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Dialog, FilterField, PageHeader } from '@/components/ui/layout';
import { Alert, Button, Field, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { TicketListView } from '../desk/TicketListView';
import { TicketThread } from '../desk/TicketThread';
import { CLIENT_DESK } from '../desk/desk-api';

type PendingAction = 'resolve' | 'escalate';

/**
 * The contact person's ticket queue.
 *
 * They are the first line: they answer, and they close out what they can. What
 * they cannot settle goes up to the owner with a reason attached — escalating
 * is a deliberate, recorded act rather than a status somebody flips, because
 * "why is this on my desk" is the first thing the owner asks.
 */
export function ClientTicketsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tenantId = params.get('tenantId') ?? '';
  const [openId, setOpenId] = useState<string | null>(() => params.get('ticket'));
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [note, setNote] = useState('');

  const profile = useQuery({
    queryKey: ['client-profile'],
    queryFn: () => get<ClientProfile>('/client/me'),
  });
  const labs = profile.data?.labs ?? [];
  const selectTenant = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('tenantId', value);
    else next.delete('tenantId');
    setParams(next, { replace: true });
  };

  const act = useMutation({
    mutationFn: (action: PendingAction) =>
      post<TicketDetail>(`/client/tickets/${openId}/${action}`, { note: note.trim() }),
    onSuccess: (ticket, action) => {
      queryClient.setQueryData(['ticket', CLIENT_DESK.tickets, ticket.id], ticket);
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['client-profile'] });
      toast.success(action === 'resolve' ? 'Ticket resolved' : 'Sent to the owner');
      setPending(null);
      setNote('');
    },
    onError: (error) => toast.error('That did not go through', describeError(error)),
  });

  if (openId) {
    return (
      <>
        <Button variant="ghost" className="mb-3" onClick={() => setOpenId(null)}>
          <ArrowLeft className="me-1.5 h-4 w-4" /> Back to tickets
        </Button>

        <TicketThread
          endpoints={CLIENT_DESK}
          ticketId={openId}
          canWriteInternal
          actions={(ticket) =>
            ticket.status === 'closed' ? null : (
              <>
                {ticket.status !== 'resolved' && (
                  <Button size="sm" onClick={() => setPending('resolve')}>
                    <CheckCircle2 className="me-1.5 h-4 w-4" /> Mark resolved
                  </Button>
                )}
                {!ticket.escalatedAt && (
                  <Button variant="secondary" size="sm" onClick={() => setPending('escalate')}>
                    <ArrowUpRight className="me-1.5 h-4 w-4" /> Refer to the owner
                  </Button>
                )}
              </>
            )
          }
        />

        <Dialog
          open={pending !== null}
          onClose={() => setPending(null)}
          title={pending === 'escalate' ? 'Refer this to the owner' : 'Mark this resolved'}
          description={
            pending === 'escalate'
              ? 'Say what you tried and what you need from them. The hospital does not see this note.'
              : 'Say what fixed it. The hospital sees this.'
          }
          footer={
            <>
              <Button variant="ghost" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button
                loading={act.isPending}
                disabled={note.trim().length < 5}
                onClick={() => pending && act.mutate(pending)}
              >
                {pending === 'escalate' ? 'Refer' : 'Resolve'}
              </Button>
            </>
          }
        >
          <Field label="Note" required>
            <Textarea rows={5} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Tickets"
        description="Raised by your hospitals, and by you on their behalf"
        actions={<Button onClick={() => navigate(`/client/tickets/new${tenantId ? `?tenantId=${tenantId}` : ''}`)}>New ticket</Button>}
      />

      {labs.length === 0 && (
        <Alert className="mb-4">No hospitals are assigned to you yet.</Alert>
      )}

      <TicketListView
        endpoints={CLIENT_DESK}
        fixedTenantId={tenantId || undefined}
        onOpen={(row) => setOpenId(row.id)}
        onResetExtraFilters={() => selectTenant('')}
        extraFilters={
          <FilterField label="Hospital">
            <Select className="w-56" value={tenantId} onChange={(e) => selectTenant(e.target.value)}>
              <option value="">All my hospitals</option>
              {labs.map((lab) => (
                <option key={lab.tenantId} value={lab.tenantId}>
                  {lab.name}
                </option>
              ))}
            </Select>
          </FilterField>
        }
      />

    </>
  );
}
